import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Modal from "../components/Modal";
import { deepLearningData } from "../data/data";

// Import tech stack icons
import streamlitIcon from "../assets/tech-icons/streamlit.png";
import googleGenAIIcon from "../assets/tech-icons/google-genai.png";
import pythonIcon from "../assets/tech-icons/python.png";
import pilIcon from "../assets/tech-icons/pil.png";
import crewAIIcon from "../assets/tech-icons/crewai.png";
import youtubeAPIIcon from "../assets/tech-icons/youtube_api.png";
import openAIIcon from "../assets/tech-icons/openai.png";
import SQLIcon from "../assets/tech-icons/sql-logo.png";
import pypdf from "../assets/tech-icons/pypdf.png";
import langchain from "../assets/tech-icons/langchain.png";
import faiss from "../assets/tech-icons/faiss.png";
import groq from "../assets/tech-icons/groq.png";
import huggingfaceAPI from "../assets/tech-icons/huggungface.png";
import nvidia from "../assets/tech-icons/nvidia_nim.jpeg";
import neo4j from "../assets/tech-icons/neo4j.png";
import astra from "../assets/tech-icons/astra.png";
import lamini from "../assets/tech-icons/lamini.png";
import pandas from "../assets/tech-icons/pandas.png";
import matplotlib from "../assets/tech-icons/matplotlib.png";
import seaborn from "../assets/tech-icons/seaborn.png";
import keras from "../assets/tech-icons/keras.png";
import numpy from "../assets/tech-icons/numpy.jpg";
import tensor from "../assets/tech-icons/tensorflow.png";
import open from "../assets/tech-icons/opencv.png";
import plotly from "../assets/tech-icons/Plotly.png";
import tesseract from "../assets/tech-icons/Tesseract.png";
import ibm from "../assets/tech-icons/ibm-watson.png";
import SkLearn from "../assets/tech-icons/sklearn.png";

const DeepLearningProjectsPage = () => {
  const [selectedTech, setSelectedTech] = useState("All");
  const [filteredProjects, setFilteredProjects] = useState(deepLearningData);
  const [modalOpen, setModalOpen] = useState(false);
  const [readmeContent, setReadmeContent] = useState("");

  // Define tech stack icons
  const techIcons = {
    Python: <img src={pythonIcon} alt="python" className="h-6 w-4" />,
    Pandas: <img src={pandas} alt="pandas" className="h-6 w-4" />,
    Matplotlib: <img src={matplotlib} alt="matplotlib" className="h-6 w-4" />,
    Seaborn: <img src={seaborn} alt="seaborn" className="h-6 w-4" />,
    Keras: <img src={keras} alt="keras" className="h-6 w-4" />,
    Numpy: <img src={numpy} alt="numpy" className="h-6 w-4" />,
    Tensorflow: <img src={tensor} alt="tensorflow" className="h-6 w-4" />,
    Pil: <img src={pilIcon} alt="Pil" className="h-6 w-4" />,
    OpenCV: <img src={open} alt="Opencv" className="h-6 w-4" />,
    Plotly: <img src={plotly} alt="Plotly" className="h-6 w-4" />,
    PyTesseract: <img src={tesseract} alt="PyTesseract" className="h-6 w-4" />,
    "IBM Watson": <img src={ibm} alt="ibm" className="h-6 w-4" />,
    SkLearn: <img src={SkLearn} alt="SkLearn" className="h-6 w-4" />,
  };

  // Filter projects based on tech stack
  useEffect(() => {
    if (selectedTech === "All") {
      setFilteredProjects(deepLearningData);
    } else {
      setFilteredProjects(
        deepLearningData.filter((project) =>
          project.techStack.includes(selectedTech)
        )
      );
    }
  }, [selectedTech]);

  // Fetch README content
  const fetchReadme = async (url) => {
    try {
      const response = await fetch(url);
      const text = await response.text();
      setReadmeContent(text);
      setModalOpen(true);
    } catch (error) {
      setReadmeContent("<p>Error loading README.</p>");
      setModalOpen(true);
    }
  };

  return (
    <div
      className="relative w-full min-h-screen bg-cover bg-center flex flex-col items-center"
      style={{
        backgroundImage: `url(/assets/background.jpg)`,
        backgroundAttachment: "fixed",
        backgroundSize: "cover",
      }}
    >
      <div className="absolute inset-0 bg-black bg-opacity-40"></div>

      <header className="relative pt-20 flex flex-col items-center z-10 px-4">
        <h1 className="text-3xl font-bold text-white drop-shadow-md">
          Deep Learning Projects
        </h1>
      </header>

      {/* Tech Stack Section */}
      <section className="relative z-10 w-full max-w-5xl px-4 mt-12">
        <h2 className="text-xl font-semibold text-white mb-4">Tech Stack</h2>
        <p className="text-gray-300 mb-4">
          Click on a tech to view projects implemented using it. Scroll
          horizontally to view the whole tech stack.
        </p>
        <div className="flex space-x-4 overflow-x-auto py-4 bg-gray-800 rounded-lg shadow px-4">
          {/* "All" Button */}
          <button
            onClick={() => setSelectedTech("All")}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg shadow text-white ${
              selectedTech === "All"
                ? "bg-blue-700"
                : "bg-gray-600 hover:bg-gray-700"
            }`}
          >
            <span>All</span>
          </button>

          {/* Dynamically render tech stacks with icons */}
          {Object.keys(techIcons).map((tech) => (
            <button
              key={tech}
              onClick={() => setSelectedTech(tech)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg shadow text-white ${
                selectedTech === tech
                  ? "bg-blue-700"
                  : "bg-gray-600 hover:bg-gray-700"
              }`}
            >
              {techIcons[tech]}
              <span>{tech}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Project Cards */}
      <main className="relative z-10 w-full max-w-5xl px-4 mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredProjects.map((project, i) => (
          <div
            key={i}
            className="bg-gray-900 rounded-lg shadow p-4 flex flex-col justify-between"
          >
            {/* Project Image */}
            <div className="w-full h-48 overflow-hidden flex justify-center items-center bg-gray-800 rounded">
              <img
                src={project.image}
                alt={project.title}
                className="object-cover h-full"
              />
            </div>

            {/* Project Title */}
            <h3 className="text-white font-semibold text-center mt-3">
              {project.title}
            </h3>

            {/* Project Description */}
            <p className="text-gray-300 text-sm mt-2 flex-1">
              {project.description}
            </p>

            {/* Tech Stack */}
            <div className="mt-4 flex flex-wrap gap-2 justify-center">
              {project.techStack.map((tech, idx) => (
                <div
                  key={idx}
                  className="flex items-center space-x-2 bg-gray-800 px-3 py-2 rounded-lg text-white"
                >
                  {techIcons[tech]}
                  <span className="text-sm">{tech}</span>
                </div>
              ))}
            </div>

            {/* Buttons */}
            <div className="mt-4 flex space-x-4 justify-center">
              <a
                href={project.githubLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 py-2 px-4 rounded bg-green-600 hover:bg-green-700 text-white"
              >
                View Code
              </a>
              <button
                onClick={() => fetchReadme(project.readmeLink)}
                className="inline-flex items-center space-x-2 py-2 px-4 rounded bg-blue-600 hover:bg-blue-700 text-white"
              >
                View Description
              </button>
            </div>
          </div>
        ))}
      </main>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        content={readmeContent}
      />

      <Link
        to="/"
        className="py-2 px-4 rounded-lg shadow font-medium text-white bg-blue-600 hover:bg-blue-700 transition transform hover:scale-105 mt-8"
      >
        Back Home
      </Link>
    </div>
  );
};

export default DeepLearningProjectsPage;
